import { useState, useCallback, useEffect, useRef } from "react";

export const useOptimistic = <TState, TInput>(
  initialValue: TState,
  updateFn: (prevState: TState, input: TInput) => TState
): [TState, (input: TInput) => void, () => void, () => void] => {
  const rollbackState = useRef<TState | null>(null);
  const prevInitialValue = useRef<TState>(initialValue);
  const [pending, setPending] = useState(false);
  const [optimisticState, setOptimisticState] = useState<TState>(initialValue);

  useEffect(() => {
    if (!pending && initialValue !== prevInitialValue.current) {
      setOptimisticState(initialValue);
      prevInitialValue.current = initialValue;
    }
  }, [initialValue, pending]);

  const applyOptimistic = useCallback(
    (input: TInput) => {
      rollbackState.current = optimisticState;
      setOptimisticState((prev) => updateFn(prev, input));
      setPending(true);
    },
    [optimisticState, updateFn]
  );

  const resetOptimistic = useCallback(() => {
    if (rollbackState.current == null) {
      return;
    }
    setOptimisticState(rollbackState.current!);
    rollbackState.current = null;
    setPending(false);
  }, [rollbackState]);

  const finish = useCallback(() => {
    rollbackState.current = null;
    setPending(false);
  }, []);

  return [optimisticState, applyOptimistic, resetOptimistic, finish];
};
